import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { RetellWebClient } from 'retell-client-js-sdk';

const AIAssistant = ({ onClose, isAgentDetails = false, currentUser = null, formContext = null, onDescriptionGenerated = null, selectedAgentData = null }) => {
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [inputMode, setInputMode] = useState('Auto');
  const [messages, setMessages] = useState([]);
  const [chatError, setChatError] = useState(null);
  const [chatId, setChatId] = useState(null); // Track chat session ID
  const [callId, setCallId] = useState(null); // Track call session ID for voice mode
  
  // Default LLM Model: Claude 3.7 Sonnet (as shown in the UI image)
  // This will be set automatically when models are fetched
  
  // Retell agent configuration
  const TEXT_AGENT_ID = 'agent_8007862b2eeeb4b3879d4f1ea1'; // Text-based agent
  const VOICE_AGENT_ID = 'agent_b8ec3769eab0e1086344255758'; // Voice agent
  const RETELL_LLM_ID = 'llm_732d8f2115f77516d55fbd089f35';
  
  // Agent selection state
  const [selectedAgent, setSelectedAgent] = useState('text'); // 'text' or 'voice'
  const [webCallData, setWebCallData] = useState(null); // Store web call data for voice agent
  
  // Voice call state
  const [isVoiceCallActive, setIsVoiceCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [callError, setCallError] = useState('');
  const retellClient = useRef(null);
  
  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
  
  // Add state for models
  const [availableModels, setAvailableModels] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState(null);
  const [modelsDropdownOpen, setModelsDropdownOpen] = useState(false);
  const modelsDropdownRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Progress bar state
  const [progressData, setProgressData] = useState({
    elapsedSeconds: 0,
    estimatedSeconds: 30, // Default estimate: 30 seconds (0.5 minutes)
    progressPercentage: 0
  });
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const sentMessageLengthRef = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      // Set max height to allow scrolling after a certain point
      const maxHeight = 200; // Maximum height in pixels (about 8-9 lines)
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [inputMessage]);

  // Progress bar tracking effect
  useEffect(() => {
    if (isTyping) {
      // Calculate estimated time based on message length (longer messages take more time)
      const messageLength = sentMessageLengthRef.current || 0;
      // Base time: 15 seconds, add 0.1 seconds per character (max 60 seconds)
      const estimatedTime = Math.min(15 + (messageLength * 0.1), 60);
      
      startTimeRef.current = Date.now();
      setProgressData({
        elapsedSeconds: 0,
        estimatedSeconds: estimatedTime,
        progressPercentage: 0
      });

      // Update progress every 100ms
      progressIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = (Date.now() - startTimeRef.current) / 1000; // Convert to seconds
          const estimated = estimatedTime;
          const progress = Math.min((elapsed / estimated) * 100, 95); // Cap at 95% until response arrives
          
          setProgressData({
            elapsedSeconds: elapsed,
            estimatedSeconds: estimated,
            progressPercentage: progress
          });
        }
      }, 100);
    } else {
      // Clear interval when typing stops
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      // Reset progress
      setProgressData({
        elapsedSeconds: 0,
        estimatedSeconds: 30,
        progressPercentage: 0
      });
      startTimeRef.current = null;
      sentMessageLengthRef.current = 0;
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isTyping]);

  // Function to detect if user is asking about another user's data
  const detectOtherUserDataQuery = (message, currentUserEmail, currentUserId, currentUserFullName) => {
    const lowerMessage = message.toLowerCase().trim();
    
    // Get current user identifiers for comparison
    const currentUserEmailLower = (currentUserEmail || '').toLowerCase();
    const currentUserFullNameLower = (currentUserFullName || '').toLowerCase();
    
    // 🔒 CRITICAL PRIVACY CHECK: Block queries asking for user counts or statistics
    // This blocks queries like "list total number of users", "how many users", "total users", etc.
    // User counts and statistics should not be displayed due to privacy policy
    const userCountQueryPatterns = [
      /(?:list|show|give|provide|get|fetch|retrieve|access|view|see|tell|display|share|send|count|number|how\s+many).*(?:total|all|current|registered|active).*(?:users?|user\s+count|number\s+of\s+users?)/i,
      /(?:total|all|current|registered|active).*(?:users?|user\s+count|number\s+of\s+users?)/i,
      /(?:how\s+many|what\s+is\s+the\s+number\s+of|what\s+is\s+the\s+count\s+of|what\s+is\s+the\s+total\s+of).*(?:users?)/i,
      /(?:users?|user\s+count|number\s+of\s+users?).*(?:total|all|current|registered|active|count|number|how\s+many)/i,
      /(?:list|show|give|provide|get|fetch|retrieve|access|view|see|tell|display|share|send).*(?:total|all|current|registered|active).*(?:number|count).*(?:of|for).*(?:users?)/i
    ];
    
    // Check if query matches user count request patterns
    for (const pattern of userCountQueryPatterns) {
      if (pattern.test(message)) {
        console.log(`🔒 [PRIVACY] BLOCKED: Query asks for user count or statistics: "${message}"`);
        return true;
      }
    }
    
    // 🔒 CRITICAL PRIVACY CHECK: Block queries asking for code
    // This blocks queries like "show me the code", "give me the source code", etc.
    // Code should not be displayed due to privacy policy and terms and conditions
    const codeQueryPatterns = [
      /(?:show|give|provide|get|fetch|retrieve|access|view|see|tell|display|share|send|let\s+me\s+(?:see|get|view)).*(?:the\s+)?(?:code|source\s+code|sourcecode|source|codebase|code\s+base|implementation|script|programming\s+code)/i,
      /(?:code|source\s+code|sourcecode|source|codebase|code\s+base|implementation|script|programming\s+code).*(?:show|give|provide|get|fetch|retrieve|access|view|see|tell|display|share|send)/i,
      /(?:can\s+you|please|could\s+you).*(?:show|give|provide|get|fetch|retrieve|access|view|see|tell|display|share|send).*(?:the\s+)?(?:code|source\s+code|sourcecode|source|codebase|code\s+base|implementation|script|programming\s+code)/i,
      /(?:what|how).*(?:is|does|are).*(?:the\s+)?(?:code|source\s+code|sourcecode|source|codebase|code\s+base|implementation|script|programming\s+code)/i,
      /(?:show|give|provide|get|fetch|retrieve|access|view|see|tell|display|share|send).*(?:me|us).*(?:the\s+)?(?:code|source\s+code|sourcecode|source|codebase|code\s+base|implementation|script|programming\s+code)/i
    ];
    
    // Check if query matches code request patterns
    for (const pattern of codeQueryPatterns) {
      if (pattern.test(message)) {
        console.log(`🔒 [PRIVACY] BLOCKED: Query asks for code: "${message}"`);
        return true;
      }
    }
    
    // 🔒 CRITICAL PRIVACY CHECK: Block queries asking for user information in general
    // This blocks queries like "Let me get more detailed information about the users"
    // or "show me information about users" - these should be blocked regardless of context
    const userInfoQueryPatterns = [
      /(?:get|show|give|provide|fetch|retrieve|access|view|see|tell|let\s+me\s+get).*(?:more|detailed|complete|full|entire|all|any).*(?:information|details?|data|info).*(?:about|of|for|regarding).*(?:the\s+)?users?/i,
      /(?:information|details?|data|info).*(?:about|of|for|regarding).*(?:the\s+)?users?/i,
      /(?:show|give|provide|get|fetch|retrieve|access|view|see|tell).*(?:me|us).*(?:information|details?|data|info).*(?:about|of|for|regarding).*(?:the\s+)?users?/i,
      /(?:let\s+me|can\s+you|please).*(?:get|show|give|provide|fetch|retrieve|access|view|see|tell).*(?:more|detailed|complete|full|entire|all|any).*(?:information|details?|data|info).*(?:about|of|for|regarding).*(?:the\s+)?users?/i
    ];
    
    // Check if query matches user information request patterns
    for (const pattern of userInfoQueryPatterns) {
      if (pattern.test(message)) {
        // Allow only if it's clearly about the user's own data
        const isOwnDataQuery = lowerMessage.includes('my ') || lowerMessage.includes('my own') || 
                              lowerMessage.includes('my personal') || lowerMessage.includes('my account') ||
                              lowerMessage.includes('my information') || lowerMessage.includes('my data') ||
                              lowerMessage.includes('my user') || lowerMessage.includes('my profile');
        
        if (!isOwnDataQuery) {
          console.log(`🔒 [PRIVACY] BLOCKED: Query asks for user information in general: "${message}"`);
          return true;
        }
      }
    }
    
    // 🔒 CRITICAL PRIVACY CHECK: First, check if message contains ANY email address
    // If it does, and it's different from current user, block immediately
    const emailMatch = message.match(/[\w\.-]+@[\w\.-]+\.\w+/gi);
    if (emailMatch && emailMatch.length > 0) {
      for (const email of emailMatch) {
        const emailLower = email.toLowerCase();
        // If email is mentioned and it's different from current user, block immediately
        // UNLESS the query is clearly about the user's own data (e.g., "my email", "my details")
        const isOwnDataQuery = lowerMessage.includes('my ') || lowerMessage.includes('my own') || 
                              lowerMessage.includes('my personal') || lowerMessage.includes('my account') ||
                              lowerMessage.includes('my information') || lowerMessage.includes('my data');
        
        if (emailLower !== currentUserEmailLower && emailLower !== '' && !isOwnDataQuery) {
          console.log(`🔒 [PRIVACY] BLOCKED: Query contains email address of another user: ${email} (current user: ${currentUserEmail})`);
          return true;
        }
      }
    }
    
    // 🔒 CRITICAL PRIVACY CHECK: Check for queries asking about "user email" or "email id" patterns
    // These patterns often indicate asking about another user (not "my email" or "my details")
    if ((lowerMessage.includes('user email') || lowerMessage.includes('email id') || 
         lowerMessage.includes('email address') || lowerMessage.includes('user id')) &&
        !lowerMessage.includes('my ') && !lowerMessage.includes('my own') && 
        !lowerMessage.includes('my personal') && !lowerMessage.includes('my account')) {
      // If there's any email in the message, we already checked above
      // But also check if the query structure suggests asking about another user
      if (lowerMessage.includes('give') || lowerMessage.includes('show') || 
          lowerMessage.includes('details') || lowerMessage.includes('information') ||
          lowerMessage.includes('data') || lowerMessage.includes('all')) {
        console.log(`🔒 [PRIVACY] BLOCKED: Query structure suggests asking about another user's data`);
        return true;
      }
    }
    
    // Patterns that indicate asking about another user
    // 🔒 ENHANCED PRIVACY: More comprehensive patterns to catch all variations
    const otherUserPatterns = [
      /show\s+me\s+.*(?:data|information|details|agents|phone|numbers|chatbots|analytics|billing|history)/i,
      /what\s+does\s+.*(?:have|own|use)/i,
      /tell\s+me\s+about\s+.*(?:user|account|profile)/i,
      /show\s+.*(?:user|account|profile|person).*(?:data|information|details|agents|phone|numbers|chatbots|analytics|billing)/i,
      /(?:get|fetch|retrieve|access|view|see).*(?:other|another|different).*(?:user|account|person).*(?:data|information|details|agents|phone|numbers|chatbots|analytics|billing)/i,
      /(?:user|account|person).*(?:email|id|name).*(?:data|information|details|agents|phone|numbers|chatbots|analytics|billing)/i,
      // 🔒 NEW: Catch queries asking for "all details" of a specific user
      /(?:give|show|provide|get|fetch|retrieve|access|view|see|tell).*(?:all|complete|full|entire|every).*(?:details?|information|data|info).*(?:of|for|about|regarding).*(?:user|account|person|email|id)/i,
      // 🔒 NEW: Catch queries with email addresses explicitly
      /(?:give|show|provide|get|fetch|retrieve|access|view|see|tell).*(?:all|complete|full|entire|every|any).*(?:details?|information|data|info).*(?:of|for|about|regarding).*@/i,
      // 🔒 NEW: Catch "details of user email" patterns
      /(?:details?|information|data|info).*(?:of|for|about|regarding).*(?:user|account|person).*(?:email|id|name)/i,
      // 🔒 NEW: Catch queries asking about specific email addresses
      /(?:what|show|give|provide|get|fetch|retrieve|access|view|see|tell).*(?:about|regarding|for|of).*[\w\.-]+@[\w\.-]+\.\w+/i,
      // 🔒 NEW: Catch "can you give" patterns with email
      /can\s+you\s+(?:give|show|provide|get|fetch|retrieve|access|view|see|tell).*[\w\.-]+@[\w\.-]+\.\w+/i
    ];
    
    // Check if message matches any pattern
    for (const pattern of otherUserPatterns) {
      if (pattern.test(message)) {
        // Extract potential user identifiers from the message
        const emailMatch = message.match(/[\w\.-]+@[\w\.-]+\.\w+/gi);
        const nameMatches = message.match(/\b[A-Z][a-z]+\s+[A-Z][a-z]+\b/g);
        
        // If email is mentioned and it's different from current user
        if (emailMatch && emailMatch.length > 0) {
          for (const email of emailMatch) {
            if (email.toLowerCase() !== currentUserEmailLower) {
              return true;
            }
          }
        }
        
        // If name is mentioned and it's different from current user
        if (nameMatches && nameMatches.length > 0) {
          for (const name of nameMatches) {
            if (name.toLowerCase() !== currentUserFullNameLower && currentUserFullNameLower) {
              return true;
            }
          }
        }
        
        // If pattern matches but no specific user mentioned, still flag it as potentially asking about others
        if (lowerMessage.includes('other') || lowerMessage.includes('another') || lowerMessage.includes('different user')) {
          return true;
        }
        
        // 🔒 ENHANCED: Check for queries asking about "all details" or "complete information" 
        // even if no specific user is mentioned in the pattern
        if (lowerMessage.includes('all details') || lowerMessage.includes('complete information') || 
            lowerMessage.includes('full details') || lowerMessage.includes('entire information') ||
            lowerMessage.includes('all information') || lowerMessage.includes('every detail')) {
          // If there's an email in the message, check if it's different from current user
          const emailInMessage = message.match(/[\w\.-]+@[\w\.-]+\.\w+/gi);
          if (emailInMessage && emailInMessage.length > 0) {
            for (const email of emailInMessage) {
              if (email.toLowerCase() !== currentUserEmailLower) {
                console.log(`🔒 [PRIVACY] Detected query asking for all details of another user: ${email}`);
                return true;
              }
            }
          }
        }
      }
    }
    
    return false;
  };

  // Enhanced fallback response generator
  const generateFallbackResponse = (message, formContext) => {
    const lowerMessage = message.toLowerCase();
    
    // 🔒 PRIVACY CHECK: Detect if user is asking about another user's data
    const currentUserEmail = currentUser?.email || '';
    const currentUserId = currentUser?.id || '';
    const currentUserFullName = currentUser?.full_name || currentUser?.email || '';
    const isOtherUserQuery = detectOtherUserDataQuery(message, currentUserEmail, currentUserId, currentUserFullName);
    if (isOtherUserQuery) {
      // Check if it's a code request to provide appropriate message
      const lowerMessage = message.toLowerCase();
      const isCodeRequest = /(?:code|source\s+code|sourcecode|source|codebase|code\s+base|implementation|script|programming\s+code)/i.test(message);
      
      if (isCodeRequest) {
        return 'Due to our terms and privacy policy, we cannot provide code or source code. You can only access your own data and information.';
      } else {
        return 'Due to our terms and privacy policy, you are not allowed to see data of any other user. You can only access your own data and information.';
      }
    }
    
    // Context-aware responses for description creation
    if (formContext && formContext.task === 'create_description') {
      if (lowerMessage.includes('description') || lowerMessage.includes('create') || lowerMessage.includes('help')) {
        return `I'd be happy to help you create a comprehensive description for your ${formContext.modalContext?.useType === 'business' ? 'business' : 'personal'} AI agent!

Based on your details:
- Agent Name: ${formContext.modalContext?.agentName}
- ${formContext.modalContext?.useType === 'business' ? `Business: ${formContext.modalContext?.businessName} (${formContext.modalContext?.businessTypeLabel})` : `Personal Use: ${formContext.modalContext?.personalReason}`}

Here's a suggested description template:

**Agent Description:**
You are ${formContext.modalContext?.agentName}, ${formContext.modalContext?.useType === 'business' ? `an AI assistant for ${formContext.modalContext?.businessName}, a ${formContext.modalContext?.businessTypeLabel} business.` : 'a personal AI assistant.'}

**Primary Services:**
${formContext.modalContext?.useType === 'business' ? 
`- Customer service and support
- Appointment scheduling
- Product/service information
- Business inquiries handling
- Call routing and management` :
`- Personal task management
- Information assistance
- Appointment scheduling
- Daily reminders
- General support`}

**Key Capabilities:**
- Professional and friendly communication
- Accurate information provision
- Call transfer when needed
- 24/7 availability
- Multilingual support

Would you like me to customize this further or help you create the chatbot configuration as well?`;
      }
      
      if (lowerMessage.includes('chatbot') || lowerMessage.includes('bot') || lowerMessage.includes('agent')) {
        return `Great! Let me help you create a chatbot configuration for your ${formContext.modalContext?.useType === 'business' ? 'business' : 'personal'} agent.

**Chatbot Configuration for ${formContext.modalContext?.agentName}:**

**Voice Settings:**
- Voice: Professional and friendly
- Language: English (US)
- Speed: Natural conversation pace

**Call Handling:**
- Greeting: Personalized welcome message
- Transfer scenarios: Based on your business needs
- After hours: Professional voicemail

**Features to Enable:**
- Call recording and transcription
- Real-time call monitoring
- Analytics and reporting
- Webhook integration for custom responses

**Business Hours:**
- Set your operating hours
- Custom after-hours message
- Holiday scheduling

Would you like me to help you configure any specific aspect of your chatbot?`;
      }
    }
    
    // General chatbot factory responses
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return `Hello! I'm your Chatbot Factory AI Assistant. I can help you with:
- Creating and managing AI agents
- Setting up phone numbers and call routing
- Configuring chatbot personalities and responses
- Analytics and call monitoring
- Billing and account management

What would you like to work on today?`;
    }
    
    if (lowerMessage.includes('phone') || lowerMessage.includes('number') || lowerMessage.includes('call')) {
      return `I can help you with phone number management! Here's what I can assist with:

**Phone Number Features:**
- Purchase new phone numbers
- Configure call routing
- Set up transfer scenarios
- Manage verified caller lists
- Configure after-hours handling

**Call Management:**
- Real-time call monitoring
- Call recording and transcription
- Analytics and reporting
- Webhook integration

What specific phone number or call feature would you like to set up?`;
    }
    
    if (lowerMessage.includes('analytics') || lowerMessage.includes('report') || lowerMessage.includes('data')) {
      return `I can help you with analytics and reporting! Here's what's available:

**Call Analytics:**
- Call volume and duration
- Success rates and outcomes
- Peak calling times
- Caller demographics

**Performance Metrics:**
- Response accuracy
- Customer satisfaction
- Transfer rates
- Cost analysis

**Reports Available:**
- Daily, weekly, monthly summaries
- Custom date ranges
- Export to CSV/PDF
- Real-time dashboards

What analytics would you like to explore?`;
    }
    
    if (lowerMessage.includes('billing') || lowerMessage.includes('cost') || lowerMessage.includes('price')) {
      return `I can help you with billing and cost management! Here's what I can show you:

**Billing Information:**
- Current usage and costs
- Phone number charges
- Call minutes and costs
- Feature usage fees

**Cost Optimization:**
- Usage recommendations
- Feature suggestions
- Plan comparisons
- Budget tracking

**Payment Management:**
- Payment methods
- Billing history
- Invoice downloads
- Auto-pay settings

What billing information would you like to see?`;
    }
    
    if (lowerMessage.includes('setting') || lowerMessage.includes('config') || lowerMessage.includes('setup')) {
      return `I can help you configure your chatbot settings! Here's what you can customize:

**Agent Settings:**
- Voice and personality
- Response templates
- Call handling rules
- Transfer scenarios

**System Settings:**
- Webhook configurations
- API integrations
- Notification preferences
- Security settings

**Account Settings:**
- User management
- Billing preferences
- Data retention
- Privacy controls

What settings would you like to configure?`;
    }
    
    if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
      return `You're welcome! I'm here to help you get the most out of your Chatbot Factory. 

Is there anything else you'd like to know about:
- Creating new agents
- Managing existing chatbots
- Phone number setup
- Analytics and reporting
- Billing and costs

Feel free to ask me anything!`;
    }
    
    // Default response for unrecognized messages
    return `I understand you're asking about: "${message}". 

I'm here to help you with your Chatbot Factory operations. I can assist with:
- Creating and managing AI agents
- Phone number and call management
- Analytics and reporting
- Billing and account settings
- Technical support and troubleshooting

Could you be more specific about what you'd like help with? For example:
- "Help me create a new agent"
- "Show me my call analytics"
- "Configure phone number settings"
- "Help with billing questions"`;
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === '') return;

    const userMessage = inputMessage.trim();
    console.log('🚀 [AI ASSISTANT] Sending message:', userMessage);
    console.log('📊 [AI ASSISTANT] Current session state:', {
      chatId,
      callId,
      selectedAgent,
      userEmail: currentUser?.email
    });
    
    // Store message length for progress calculation
    sentMessageLengthRef.current = userMessage.length;
    
    setInputMessage('');
    setIsTyping(true);
    setChatError(null);

    // Add user message to chat
    const newUserMessage = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      // Send message to Retell agent via backend
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          agent_id: selectedAgent === 'text' ? TEXT_AGENT_ID : VOICE_AGENT_ID,
          user_email: currentUser?.email || selectedAgentData?.user_email || 'assistant@example.com',
          user_id: currentUser?.id || selectedAgentData?.user_id,
          user_full_name: currentUser?.full_name || currentUser?.email || selectedAgentData?.user_email || 'Unknown User',
          chat_id: chatId, // Include chat session ID if exists
          // Add user context for the AI assistant
          user_context: {
            user_id: currentUser?.id || selectedAgentData?.user_id,
            email: currentUser?.email || selectedAgentData?.user_email,
            full_name: currentUser?.full_name || currentUser?.email || selectedAgentData?.user_email || 'Unknown User',
            is_agent_details: isAgentDetails,
            form_context: formContext || (selectedAgentData ? {
              agent_id: selectedAgentData.agent_id,
              agent_name: selectedAgentData.agent_name,
              is_agent_details: true
            } : null)
          }
        }),
      });

      if (!response.ok) {
        // Check if it's a privacy violation error
        if (response.status === 403) {
          const errorData = await response.json();
          if (errorData.privacy_violation) {
            // Add privacy violation message to chat
            const privacyMessage = {
              id: Date.now() + 1,
              type: 'error',
              content: errorData.error || errorData.response || 'Due to our terms and privacy policy, you are not allowed to see data of any other user. You can only access your own data and information.',
              timestamp: new Date().toISOString()
            };
            setMessages(prev => {
              const filteredMessages = prev.filter(msg => !msg.isProcessing);
              return [...filteredMessages, privacyMessage];
            });
            setIsTyping(false);
            return;
          }
        }
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      console.log('📥 [AI ASSISTANT] Response received:', {
        success: data.success,
        hasResponse: !!data.response,
        chatId: data.chat_id,
        callId: data.call_id,
        agentId: data.agent_id,
        fullResponse: data
      });
      
      // Check for privacy violation in successful response
      if (data.privacy_violation) {
        const privacyMessage = {
          id: Date.now() + 1,
          type: 'error',
          content: data.error || data.response || 'Due to our terms and privacy policy, you are not allowed to see data of any other user. You can only access your own data and information.',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => {
          const filteredMessages = prev.filter(msg => !msg.isProcessing);
          return [...filteredMessages, privacyMessage];
        });
        setIsTyping(false);
        return;
      }
      
      if (data.success) {
        // Store chat_id and call_id for future messages
        if (data.chat_id && !chatId) {
          setChatId(data.chat_id);
          console.log(`✅ [AI ASSISTANT] Chat session established: ${data.chat_id}`);
          console.log(`📊 [AI ASSISTANT] Session will be tracked for billing`);
        }
        
        if (data.call_id && !callId) {
          setCallId(data.call_id);
          console.log(`✅ [AI ASSISTANT] Call session established: ${data.call_id}`);
          console.log(`📊 [AI ASSISTANT] Voice session will be tracked for billing`);
        }
        
        // Add AI response to chat - handle both response formats
        const responseContent = data.response || data.message || data.answer || 'I received your message but couldn\'t generate a response. Please try again.';
        const newAIMessage = {
          id: Date.now() + 1,
          type: 'ai',
          content: responseContent,
          timestamp: data.timestamp || new Date().toISOString()
        };
        setMessages(prev => {
          // Remove any processing messages and add the new AI response
          const filteredMessages = prev.filter(msg => !msg.isProcessing);
          return [...filteredMessages, newAIMessage];
        });
        console.log(`💬 [AI ASSISTANT] AI response added to chat:`, responseContent);
        
        // If this is a description generation task and we have a callback, pass the response
        if (formContext && formContext.task === 'create_description' && onDescriptionGenerated && responseContent) {
          console.log('🔄 [AI ASSISTANT] Passing generated description to parent component');
          console.log('📊 [AI ASSISTANT] Form context:', formContext);
          console.log('📊 [AI ASSISTANT] Response content length:', responseContent.length);
          onDescriptionGenerated(responseContent);
        } else {
          console.log('⚠️ [AI ASSISTANT] Not passing description - conditions not met:', {
            hasFormContext: !!formContext,
            task: formContext?.task,
            hasCallback: !!onDescriptionGenerated,
            hasResponse: !!responseContent
          });
        }
      } else {
        // Handle error response
        const errorMessage = data.error || data.message || 'Failed to get response from AI Assistant';
        console.error('❌ [AI ASSISTANT] Error response:', errorMessage);
        
        // Add error message to chat
        const errorChatMessage = {
          id: Date.now() + 1,
          type: 'error',
          content: `Sorry, I encountered an error: ${errorMessage}. Please try again.`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => {
          // Remove any processing messages and add the error message
          const filteredMessages = prev.filter(msg => !msg.isProcessing);
          return [...filteredMessages, errorChatMessage];
        });
      }

    } catch (error) {
      console.error('❌ [AI ASSISTANT] Error sending message:', error);
      console.error('❌ [AI ASSISTANT] Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      
      // Use fallback response instead of showing error
      console.log('🔄 [AI ASSISTANT] Using fallback response system');
      const fallbackResponse = generateFallbackResponse(userMessage, formContext);
      
      // Add fallback response to chat
      const fallbackMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: fallbackResponse,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => {
        // Remove any processing messages and add the fallback response
        const filteredMessages = prev.filter(msg => !msg.isProcessing);
        return [...filteredMessages, fallbackMessage];
      });
      console.log(`💬 [AI ASSISTANT] Fallback response added to chat`);
      
      // If this is a description generation task and we have a callback, pass the fallback response
      if (formContext && formContext.task === 'create_description' && onDescriptionGenerated && fallbackResponse) {
        console.log('🔄 [AI ASSISTANT] Passing fallback description to parent component');
        console.log('📊 [AI ASSISTANT] Fallback response length:', fallbackResponse.length);
        onDescriptionGenerated(fallbackResponse);
      } else {
        console.log('⚠️ [AI ASSISTANT] Not passing fallback description - conditions not met:', {
          hasFormContext: !!formContext,
          task: formContext?.task,
          hasCallback: !!onDescriptionGenerated,
          hasFallbackResponse: !!fallbackResponse
        });
      }
      
      // Clear any previous errors
      setChatError(null);
    } finally {
      // Set progress to 100% before stopping typing indicator
      setProgressData(prev => ({
        ...prev,
        progressPercentage: 100
      }));
      // Small delay to show completion before hiding
      setTimeout(() => {
        setIsTyping(false);
      }, 300);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAddContext = () => {
    // Handle adding context functionality
    console.log('Add context clicked');
  };

  const handleFileAttachment = () => {
    // Handle file attachment functionality
    console.log('File attachment clicked');
  };

  const handleClose = async () => {
    // End any existing voice call
    if (isVoiceCallActive) {
      endVoiceCall();
    }
    
    // End session only when explicitly closing the widget; do not clear messages
    try {
      await endChatSession();
    } catch (e) {
      console.error('⚠️ [AI ASSISTANT] Error during close cleanup (ignored):', e);
    }
    
    // Call the original onClose function
    onClose();
    
    // Refresh the page after closing the assistant
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Function to end chat session and calculate costs
  const endChatSession = async () => {
    if (chatId || callId) {
      try {
        console.log('🔚 [AI ASSISTANT] Starting session cleanup...');
        console.log('📊 [AI ASSISTANT] Session details to end:', {
          chatId,
          callId,
          userEmail: currentUser?.email,
          selectedAgent
        });
        
        const requestBody = {
          chat_id: chatId,
          call_id: callId,
          user_email: currentUser?.email || selectedAgentData?.user_email || 'assistant@example.com'
        };
        
        console.log('📤 [AI ASSISTANT] Sending end session request:', requestBody);
        
        const response = await fetch(`${API_BASE_URL}/api/chat/end`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          const endData = await response.json();
          console.log('✅ [AI ASSISTANT] Session ended successfully:', endData);
          console.log('💰 [AI ASSISTANT] Costs calculated and stored in database');
        } else {
          const errorData = await response.text();
          console.error('⚠️ [AI ASSISTANT] Failed to end session:', errorData);
        }
      } catch (error) {
        console.error('❌ [AI ASSISTANT] Error ending session:', error);
      }
    } else {
      console.log('ℹ️ [AI ASSISTANT] No active session to end');
    }
  };

  // Setup event listeners for Retell client
  const setupEventListeners = () => {
    const client = retellClient.current;
    client.on('call_started', () => {
      setCallStatus('Call connected! You can now speak.');
      setIsVoiceCallActive(true);
      setIsConnecting(false);
    });
    client.on('call_ended', () => {
      setCallStatus('Call ended.');
      setIsVoiceCallActive(false);
      setIsConnecting(false);
    });
    client.on('agent_start_talking', () => {
      setCallStatus('Agent is speaking...');
    });
    client.on('agent_stop_talking', () => {
      setCallStatus('Listening... You can speak now.');
    });
    client.on('error', (error) => {
      setCallError(`Call error: ${error.message || error}`);
      setIsVoiceCallActive(false);
      setIsConnecting(false);
    });
    client.on('update', (update) => {
      // Optionally handle real-time updates
    });
  };

  // Start voice call
  const startVoiceCall = async () => {
    if (!webCallData?.access_token) {
      setCallError('No web call data available');
      return;
    }
    
    setIsConnecting(true);
    setCallError('');
    setCallStatus('Connecting...');
    
    try {
      console.log('🎤 [VOICE CALL] Starting voice call...');
      console.log('📊 [VOICE CALL] Web call data:', {
        hasAccessToken: !!webCallData.access_token,
        callId: webCallData.call_id,
        agentId: webCallData.agent_id
      });
      
      // Check if RetellWebClient is available
      if (typeof RetellWebClient === 'undefined') {
        throw new Error('RetellWebClient is not available. Please check if retell-client-js-sdk is properly installed.');
      }
      
      // Check microphone permissions
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop()); // Stop the test stream
        console.log('✅ [VOICE CALL] Microphone permission granted');
      } catch (permError) {
        console.error('❌ [VOICE CALL] Microphone permission denied:', permError);
        throw new Error('Microphone access is required for voice calls. Please allow microphone access and try again.');
      }
      
      retellClient.current = new RetellWebClient();
      setupEventListeners();
      
      console.log('🎤 [VOICE CALL] Calling startCall with access token...');
      await retellClient.current.startCall({
        accessToken: webCallData.access_token,
        sampleRate: 24000,
        emitRawAudioSamples: false,
      });
      
      console.log('✅ [VOICE CALL] Voice call started successfully');
    } catch (error) {
      console.error('❌ [VOICE CALL] Error starting call:', error);
      console.error('❌ [VOICE CALL] Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      // Provide more specific error messages
      let errorMessage = 'Error starting call';
      if (error.message) {
        errorMessage = `Call error: ${error.message}`;
      } else if (error.name) {
        errorMessage = `Call error: ${error.name}`;
      }
      
      setCallError(errorMessage);
      setIsConnecting(false);
    }
  };

  // End voice call
  const endVoiceCall = () => {
    if (retellClient.current) {
      try {
        retellClient.current.stopCall();
      } catch (error) {}
    }
    setCallStatus('Call ended.');
    setIsVoiceCallActive(false);
    setIsConnecting(false);
  };

  // Handle agent selection change
  const handleAgentChange = async (agentType) => {
    if (agentType === selectedAgent) return;
    
    console.log(`🔄 [AI ASSISTANT] Switching agent mode:`, {
      from: selectedAgent,
      to: agentType,
      currentSessions: { chatId, callId }
    });
    
    // End any existing sessions before switching
    if (chatId || callId) {
      console.log('🔚 [AI ASSISTANT] Ending existing session before mode switch');
      await endChatSession();
    }
    
    // End any existing voice call
    if (isVoiceCallActive) {
      console.log('📞 [AI ASSISTANT] Ending active voice call');
      endVoiceCall();
    }
    
    setSelectedAgent(agentType);
    setChatId(null);
    setCallId(null);
    setMessages([]);
    setChatError(null);
    setWebCallData(null);
    setCallStatus('');
    setCallError('');
    setIsVoiceCallActive(false);
    setIsConnecting(false);
    
    console.log(`✅ [AI ASSISTANT] Switched to ${agentType} mode`);
    
    // If switching to voice agent, create a web call
    if (agentType === 'voice') {
      try {
        console.log('🎤 [AI ASSISTANT] Creating voice web call...');
        console.log('📊 [AI ASSISTANT] Voice call parameters:', {
          agentId: VOICE_AGENT_ID,
          userEmail: currentUser?.email,
          userId: currentUser?.id
        });
        
        const response = await fetch(`${API_BASE_URL}/api/ai-assistant/web-call`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agent_id: VOICE_AGENT_ID,
            user_email: currentUser?.email || selectedAgentData?.user_email || 'assistant@example.com',
            retell_llm_dynamic_variables: {
              user_id: currentUser?.id || selectedAgentData?.user_id || '',
              user_email: currentUser?.email || selectedAgentData?.user_email || '',
              user_full_name: currentUser?.full_name || currentUser?.email || selectedAgentData?.user_email || 'Unknown User',
              user_context: JSON.stringify({
                user_id: currentUser?.id || selectedAgentData?.user_id,
                email: currentUser?.email || selectedAgentData?.user_email,
                full_name: currentUser?.full_name || currentUser?.email || selectedAgentData?.user_email || 'Unknown User',
                is_agent_details: isAgentDetails,
                form_context: formContext || (selectedAgentData ? {
                  agent_id: selectedAgentData.agent_id,
                  agent_name: selectedAgentData.agent_name,
                  is_agent_details: true
                } : null)
              })
            }
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ [WEB CALL] Failed to create web call:', {
            status: response.status,
            statusText: response.statusText,
            error: errorText
          });
          throw new Error(`Failed to create web call: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('📊 [WEB CALL] Web call response:', {
          hasAccessToken: !!data.access_token,
          callId: data.call_id,
          agentId: data.agent_id
        });
        setWebCallData(data);
        console.log('✅ [AI ASSISTANT] Voice agent web call created successfully:', {
          callId: data.call_id,
          agentId: data.agent_id,
          hasAccessToken: !!data.access_token
        });
        
        // Store call_id for voice mode
        if (data.call_id) {
          setCallId(data.call_id);
          console.log(`✅ [AI ASSISTANT] Voice call session stored: ${data.call_id}`);
          console.log(`📊 [AI ASSISTANT] Voice session will be tracked for billing`);
        }
        
        // Add a system message to indicate voice mode
        const voiceMessage = {
          id: Date.now(),
          type: 'system',
          content: '🎤 Voice mode activated! Click "Start Voice Call" to begin speaking.',
          timestamp: new Date().toISOString()
        };
        setMessages([voiceMessage]);
        
      } catch (error) {
        console.error('Error creating web call:', error);
        setChatError('Failed to activate voice mode. Please try again.');
      }
    } else {
      // Switching to text mode
      const textMessage = {
        id: Date.now(),
        type: 'system',
        content: '💬 Text mode activated! You can now chat with the AI assistant.',
        timestamp: new Date().toISOString()
      };
      setMessages([textMessage]);
    }
  };

  // Fetch available models
  const fetchModels = async () => {
    setModelsLoading(true);
    setModelsError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/models`);
      if (!response.ok) {
        throw new Error('Failed to fetch models');
      }
      const data = await response.json();
      setAvailableModels(data);
      
      // Set Claude 3.7 Sonnet as default model if available (as shown in the UI image)
      if (data.length > 0 && !currentModel) {
        const claude37Sonnet = data.find(model => 
          model.name && model.name === 'Claude 3.7 Sonnet'
        );
        
        if (claude37Sonnet) {
          console.log('🎯 [AI ASSISTANT] Setting Claude 3.7 Sonnet as default model:', claude37Sonnet.name);
          setCurrentModel(claude37Sonnet);
        } else {
          // Fallback to first model if Claude 3.7 Sonnet not found
          console.log('⚠️ [AI ASSISTANT] Claude 3.7 Sonnet not found, using first available model:', data[0].name);
          setCurrentModel(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      setModelsError(error.message);
    } finally {
      setModelsLoading(false);
    }
  };

  // Handle model selection
  const handleModelSelect = (model) => {
    setCurrentModel(model);
    setModelsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelsDropdownRef.current && !modelsDropdownRef.current.contains(event.target)) {
        setModelsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch models on component mount
  useEffect(() => {
    fetchModels();
  }, []);

  // Initialize with text mode message or form context
  useEffect(() => {
    if (messages.length === 0 && selectedAgent === 'text') {
      if (formContext && formContext.task === 'create_description') {
        // Pre-fill the input with context message for user to send manually
        const contextMessage = formContext.message;
        setInputMessage(contextMessage);
        
        // Add a system message to guide the user
        const systemMessage = {
          id: Date.now(),
          type: 'system',
          content: '💬 I\'ve prepared a message for you based on your agent details. Please review it in the input field below and click the ↑ button when ready.',
          timestamp: new Date().toISOString()
        };
        setMessages([systemMessage]);
        console.log('🔧 [AI ASSISTANT] Added system message for form context:', systemMessage);
        console.log('🔧 [AI ASSISTANT] Messages should now have length 1');
      } else {
        const textMessage = {
          id: Date.now(),
          type: 'system',
          content: 'Hello! I\'m here to help you with your chatbot operations, phone number management, and any questions about the platform. How can I assist you today?',
          timestamp: new Date().toISOString()
        };
        setMessages([textMessage]);
        console.log('🔧 [AI ASSISTANT] Added system message for text mode:', textMessage);
        console.log('🔧 [AI ASSISTANT] Messages should now have length 1');
      }
    }
  }, [selectedAgent, formContext]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      // End any existing voice call
      if (retellClient.current) {
        try { retellClient.current.stopCall(); } catch {}
        retellClient.current = null;
      }
      
      // End any existing chat session
      if (chatId || callId) {
        endChatSession();
      }
    };
  }, [chatId, callId]);

  // Handle page refresh/unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (chatId || callId) {
        console.log('🚨 [AI ASSISTANT] Page unload detected - cleaning up sessions');
        console.log('📊 [AI ASSISTANT] Sessions to cleanup:', { chatId, callId });
        
        // Use navigator.sendBeacon for reliable cleanup on page unload
        const data = new Blob([JSON.stringify({
          chat_id: chatId,
          call_id: callId,
          user_email: currentUser?.email || selectedAgentData?.user_email || 'assistant@example.com'
        })], { type: 'application/json' });
        
        try {
          const beaconSent = navigator.sendBeacon(`${API_BASE_URL}/api/chat/end`, data);
          console.log('📡 [AI ASSISTANT] Cleanup beacon sent:', beaconSent);
        } catch (error) {
          console.error('❌ [AI ASSISTANT] Error sending beacon for cleanup:', error);
        }
      } else {
        console.log('ℹ️ [AI ASSISTANT] No active sessions to cleanup on page unload');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [chatId, callId, currentUser?.email]);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      maxHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#fff',
      overflow: 'hidden',
      position: 'relative',
      touchAction: 'manipulation' // Improves touch response on mobile
    }}>
      {/* Chat Interface */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '10px 20px',
        gap: '12px',
        minHeight: 0, // Important for flex child to shrink
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '8px',
          borderBottom: '1px solid #e5e7eb',
          position: 'relative'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#111827',
            flex: 1,
            textAlign: 'center'
          }}>
            AI Assistant
          </h3>
          {/* Close Button - Show when onClose is provided */}
          {onClose && (
            <button
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                lineHeight: '1',
                padding: 0,
                transition: 'all 0.2s ease',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#111827';
                e.target.style.background = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#6b7280';
                e.target.style.background = 'transparent';
              }}
              title="Close Assistant"
            >
              ×
            </button>
          )}
        </div>

        {/* Agent Selection */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          padding: '6px 0'
        }}>
          <button
            onClick={() => handleAgentChange('text')}
            style={{
              background: selectedAgent === 'text' ? '#667eea' : '#f3f4f6',
              color: selectedAgent === 'text' ? '#fff' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              if (selectedAgent !== 'text') {
                e.target.style.background = '#e5e7eb';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedAgent !== 'text') {
                e.target.style.background = '#f3f4f6';
              }
            }}
          >
            <i className="fas fa-comments" style={{ fontSize: '0.8rem' }}></i>
            Text Chat
          </button>
          <button
            onClick={() => handleAgentChange('voice')}
            style={{
              background: selectedAgent === 'voice' ? '#667eea' : '#f3f4f6',
              color: selectedAgent === 'voice' ? '#fff' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              if (selectedAgent !== 'voice') {
                e.target.style.background = '#e5e7eb';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedAgent !== 'voice') {
                e.target.style.background = '#f3f4f6';
              }
            }}
          >
            <i className="fas fa-microphone" style={{ fontSize: '0.8rem' }}></i>
            Voice Chat
          </button>
        </div>

        {/* Chat Messages Area */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '8px 0',
          minHeight: 0, // Important for flex child to shrink
          maxHeight: 'calc(100vh - 300px)', // Ensure it doesn't exceed viewport
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          touchAction: 'pan-y' // Enable vertical touch scrolling
        }}>
          {/* Welcome Message (only show if no messages and no system messages) */}
          {messages.length === 0 && (() => {
            console.log('🔍 [AI ASSISTANT] Showing welcome message - messages.length:', messages.length, 'selectedAgent:', selectedAgent);
            return true;
          })() && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start'
            }}>
              <div style={{
                maxWidth: '80%',
                padding: '12px 16px',
                borderRadius: '12px 12px 12px 4px',
                background: '#f3f4f6',
                color: '#374151',
                fontSize: '0.9rem',
                lineHeight: '1.4',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}>
                <ReactMarkdown
                  components={{
                    p: ({children}) => <p style={{margin: '0 0 8px 0', lineHeight: '1.4'}}>{children}</p>,
                    strong: ({children}) => <strong style={{fontWeight: 'bold'}}>{children}</strong>,
                    em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>
                  }}
                >
                  {selectedAgent === 'voice' 
                    ? "🎤 Voice mode activated! You can now speak with the AI assistant. Click the microphone button to start a voice conversation."
                    : isAgentDetails 
                      ? "Hello! I'm here to help you with configuring this agent, managing its settings, and optimizing its performance. How can I assist you today?"
                      : "Hello! I'm here to help you with your chatbot operations, phone number management, and any questions about the platform. How can I assist you today?"
                  }
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Chat Messages */}
          {(() => {
            console.log('🔍 [AI ASSISTANT] Rendering messages:', {
              totalMessages: messages.length,
              messages: messages.map(m => ({ id: m.id, type: m.type, content: m.content.substring(0, 50) + '...' }))
            });
            return messages;
          })().map((message) => (
            <div key={message.id} style={{
              display: 'flex',
              justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
            }}>
              <div style={{
                maxWidth: '80%',
                padding: '12px 16px',
                borderRadius: message.type === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                background: message.type === 'user' ? '#667eea' : 
                           message.type === 'error' ? '#fef2f2' : 
                           message.type === 'system' ? '#f0f9ff' : '#f3f4f6',
                color: message.type === 'user' ? '#fff' : 
                       message.type === 'error' ? '#dc2626' : 
                       message.type === 'system' ? '#0369a1' : '#374151',
                fontSize: '0.9rem',
                lineHeight: '1.4',
                border: message.type === 'error' ? '1px solid #fecaca' : 
                       message.type === 'system' ? '1px solid #0ea5e9' : 'none',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}>
                {message.type === 'user' ? (
                  <span>{message.content}</span>
                ) : (
                  <ReactMarkdown
                    components={{
                      // Custom styling for markdown elements
                      p: ({children}) => <p style={{margin: '0 0 8px 0', lineHeight: '1.4'}}>{children}</p>,
                      h1: ({children}) => <h1 style={{margin: '8px 0', fontSize: '1.2rem', fontWeight: 'bold'}}>{children}</h1>,
                      h2: ({children}) => <h2 style={{margin: '8px 0', fontSize: '1.1rem', fontWeight: 'bold'}}>{children}</h2>,
                      h3: ({children}) => <h3 style={{margin: '8px 0', fontSize: '1rem', fontWeight: 'bold'}}>{children}</h3>,
                      h4: ({children}) => <h4 style={{margin: '8px 0', fontSize: '0.95rem', fontWeight: 'bold'}}>{children}</h4>,
                      h5: ({children}) => <h5 style={{margin: '8px 0', fontSize: '0.9rem', fontWeight: 'bold'}}>{children}</h5>,
                      h6: ({children}) => <h6 style={{margin: '8px 0', fontSize: '0.85rem', fontWeight: 'bold'}}>{children}</h6>,
                      ul: ({children}) => <ul style={{margin: '8px 0', paddingLeft: '20px'}}>{children}</ul>,
                      ol: ({children}) => <ol style={{margin: '8px 0', paddingLeft: '20px'}}>{children}</ol>,
                      li: ({children}) => <li style={{margin: '4px 0', lineHeight: '1.4'}}>{children}</li>,
                      strong: ({children}) => <strong style={{fontWeight: 'bold'}}>{children}</strong>,
                      em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>,
                      code: ({children}) => <code style={{
                        background: 'rgba(0,0,0,0.1)',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontFamily: 'monospace'
                      }}>{children}</code>,
                      pre: ({children}) => <pre style={{
                        background: 'rgba(0,0,0,0.1)',
                        padding: '8px',
                        borderRadius: '6px',
                        overflow: 'auto',
                        fontSize: '0.85rem',
                        fontFamily: 'monospace',
                        margin: '8px 0'
                      }}>{children}</pre>,
                      blockquote: ({children}) => <blockquote style={{
                        borderLeft: '3px solid #667eea',
                        paddingLeft: '12px',
                        margin: '8px 0',
                        fontStyle: 'italic',
                        color: 'rgba(0,0,0,0.7)'
                      }}>{children}</blockquote>
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          ))}

          {/* Processing Indicator with Progress Bar */}
          {isTyping && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start'
            }}>
              <div style={{
                maxWidth: '80%',
                padding: '12px 16px',
                borderRadius: '12px 12px 12px 4px',
                background: '#f3f4f6',
                color: '#374151',
                fontSize: '0.9rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                minWidth: '250px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fas fa-spinner fa-spin" style={{ color: '#3b82f6' }}></i>
                  <span>Processing your request...</span>
                </div>
                
                {/* Progress Bar */}
                <div style={{
                  width: '100%',
                  background: '#e5e7eb',
                  borderRadius: '10px',
                  height: '6px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <div style={{
                    width: `${progressData.progressPercentage}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                    borderRadius: '10px',
                    transition: 'width 0.1s ease',
                    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                  }}></div>
                </div>
                
                {/* Time Estimate */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginTop: '2px'
                }}>
                  <span>
                    {progressData.elapsedSeconds < 60 
                      ? `Elapsed: ${Math.floor(progressData.elapsedSeconds)}s`
                      : `Elapsed: ${(progressData.elapsedSeconds / 60).toFixed(1)} min`
                    }
                  </span>
                  <span>
                    {progressData.estimatedSeconds < 60
                      ? `Est: ${Math.ceil(progressData.estimatedSeconds)}s`
                      : `Est: ${(progressData.estimatedSeconds / 60).toFixed(1)} min`
                    }
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Voice Call Status */}
          {selectedAgent === 'voice' && (callStatus || callError) && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '8px 16px'
            }}>
              <div style={{
                background: callError ? '#fef2f2' : '#f0fdf4',
                border: callError ? '1px solid #fecaca' : '1px solid #bbf7d0',
                borderRadius: '8px',
                padding: '12px 16px',
                color: callError ? '#dc2626' : '#166534',
                fontSize: '0.9rem',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '90%',
                textAlign: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <i className={`fas ${callError ? 'fa-exclamation-triangle' : 'fa-microphone'}`} 
                     style={{ color: callError ? '#dc2626' : '#16a34a' }}></i>
                  <strong>{callError ? 'Call Error' : 'Voice Call Status'}</strong>
                </div>
                <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                  {callError || callStatus}
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {chatError && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '8px 16px'
            }}>
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '8px 12px',
                color: '#dc2626',
                fontSize: '0.8rem',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                <ReactMarkdown
                  components={{
                    p: ({children}) => <span style={{lineHeight: '1.4'}}>{children}</span>,
                    strong: ({children}) => <strong style={{fontWeight: 'bold'}}>{children}</strong>,
                    em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>
                  }}
                >
                  {chatError}
                </ReactMarkdown>
              </div>
            </div>
          )}
          
          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>


        {/* Input Field or Voice Call Controls */}
        {selectedAgent === 'voice' ? (
          /* Voice Call Controls */
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: '1px solid #059669',
            borderRadius: '12px',
            padding: '20px',
            position: 'relative',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* Voice Call Status */}
            <div style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '1rem',
              textAlign: 'center',
              fontWeight: '500'
            }}>
              {isVoiceCallActive ? 'Voice Call Active' : 'Ready to Start Voice Call'}
            </div>
            
            {/* Voice Call Button */}
            {!isVoiceCallActive ? (
              <button
                onClick={startVoiceCall}
                disabled={isConnecting || !webCallData?.access_token}
                style={{
                  background: isConnecting ? 'rgba(255, 255, 255, 0.3)' : '#fff',
                  color: isConnecting ? 'rgba(255, 255, 255, 0.7)' : '#059669',
                  border: 'none',
                  borderRadius: '50px',
                  padding: '16px 32px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: isConnecting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s ease',
                  boxShadow: isConnecting ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!isConnecting) {
                    e.target.style.transform = 'scale(1.02)';
                    e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isConnecting) {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }
                }}
                title="Start voice call"
              >
                <i className={`fas ${isConnecting ? 'fa-spinner fa-spin' : 'fa-microphone'}`} style={{ fontSize: '1.2rem' }}></i>
                {isConnecting ? 'Connecting...' : 'Start Voice Call'}
              </button>
            ) : (
              <button
                onClick={endVoiceCall}
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50px',
                  padding: '16px 32px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 6px 16px rgba(220, 38, 38, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                }}
                title="End voice call"
              >
                <i className="fas fa-phone-slash" style={{ fontSize: '1.2rem' }}></i>
                End Voice Call
              </button>
            )}
            
            {/* Instructions */}
            <div style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem',
              textAlign: 'center',
              lineHeight: '1.4'
            }}>
              <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
              Make sure your microphone is enabled. The call will connect through your browser.
            </div>
          </div>
        ) : (
          /* Text Input Field */
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: '1px solid #5a67d8',
            borderRadius: '12px',
            padding: '16px',
            position: 'relative',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
            minHeight: '180px'
          }}>
            {/* @ Add Context Button */}
            <div style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              zIndex: 2
            }}>
              <button
                onClick={handleAddContext}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  fontSize: '0.8rem',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
              >
                <span style={{ fontSize: '0.9rem' }}>@</span>
                Add Context
              </button>
            </div>

            {/* Placeholder Text */}
            {inputMessage === '' && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                pointerEvents: 'none',
                zIndex: 1
              }}>
                Plan, search, build anything
              </div>
            )}

            {/* Input Field */}
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              style={{
                width: '100%',
                minHeight: '100px',
                maxHeight: '200px',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '0.9rem',
                color: '#fff',
                resize: 'none',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                paddingTop: '40px',
                paddingBottom: '70px',
                paddingLeft: '12px',
                paddingRight: '12px',
                overflowY: 'auto'
              }}
              placeholder=""
            />

            {/* Bottom Controls */}
            <div style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              right: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 2
            }}>
              {/* Models Dropdown */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }} ref={modelsDropdownRef}>
                <button
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '20px',
                    padding: '6px 12px',
                    fontSize: '0.8rem',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(10px)',
                    minWidth: '120px',
                    justifyContent: 'space-between'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  }}
                  onClick={() => setModelsDropdownOpen(!modelsDropdownOpen)}
                  disabled={modelsLoading}
                >
                  {modelsLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ fontSize: '0.8rem' }}></i>
                      <span>Loading...</span>
                    </>
                  ) : currentModel ? (
                    <>
                      <i className={`${currentModel.provider === 'OpenAI' ? 'fab fa-openai' : currentModel.provider === 'Anthropic' ? 'ai ai-anthropic' : 'fas fa-robot'}`} 
                         style={{ color: currentModel.provider === 'OpenAI' ? '#6366f1' : currentModel.provider === 'Anthropic' ? '#f59e42' : '#fff', fontSize: '0.8rem' }}></i>
                      <span style={{ fontSize: '0.8rem' }}>{currentModel.name}</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-robot" style={{ fontSize: '0.8rem' }}></i>
                      <span style={{ fontSize: '0.8rem' }}>Select Model</span>
                    </>
                  )}
                  <i className={`fas fa-chevron-${modelsDropdownOpen ? 'up' : 'down'}`} style={{ fontSize: '0.7rem' }}></i>
                </button>
                
                {/* Models Dropdown Menu */}
                {modelsDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    bottom: '45px',
                    left: '12px',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                    minWidth: '280px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    padding: '8px 0',
                    zIndex: 1000
                  }}>
                    {modelsError ? (
                      <div style={{ padding: '12px 16px', textAlign: 'center', color: '#dc2626', fontSize: '0.8rem' }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
                        <ReactMarkdown
                          components={{
                            p: ({children}) => <span style={{lineHeight: '1.4'}}>{children}</span>,
                            strong: ({children}) => <strong style={{fontWeight: 'bold'}}>{children}</strong>,
                            em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>
                          }}
                        >
                          {modelsError}
                        </ReactMarkdown>
                      </div>
                    ) : availableModels.length === 0 ? (
                      <div style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
                        No models available
                      </div>
                    ) : (
                      availableModels.map((model) => (
                        <div 
                          key={model.id}
                          style={{ 
                            padding: '8px 12px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 8, 
                            cursor: 'pointer', 
                            fontWeight: 500, 
                            color: '#222',
                            background: currentModel?.id === model.id ? '#f0f9ff' : 'transparent',
                            borderLeft: currentModel?.id === model.id ? '3px solid #3b82f6' : '3px solid transparent',
                            fontSize: '0.8rem'
                          }}
                          onClick={() => handleModelSelect(model)}
                        >
                          <i className={`${model.provider === 'OpenAI' ? 'fab fa-openai' : model.provider === 'Anthropic' ? 'ai ai-anthropic' : 'fas fa-robot'}`} 
                             style={{ color: model.provider === 'OpenAI' ? '#6366f1' : model.provider === 'Anthropic' ? '#f59e42' : '#64748b', fontSize: 14 }}></i>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{model.name}</div>
                            {model.features && model.features.length > 0 && (
                              <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                                {model.features.slice(0, 2).map((feature, idx) => (
                                  <span key={idx} style={{ 
                                    background: '#f1f5f9', 
                                    color: '#64748b', 
                                    borderRadius: 4, 
                                    padding: '1px 4px', 
                                    fontSize: 10, 
                                    fontWeight: 500 
                                  }}>
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {currentModel?.id === model.id && (
                            <i className="fas fa-check" style={{ color: '#3b82f6', fontSize: 12, marginLeft: 8 }}></i>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Right Side Controls */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {/* Attachment Button - Commented out for now */}
                {/* <button
                  onClick={handleFileAttachment}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.8)',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                  }}
                  title="Attach file"
                >
                  <i className="fas fa-image" style={{ fontSize: '1rem' }}></i>
                </button> */}

                {/* Send Button */}
                <button
                  onClick={handleSendMessage}
                  disabled={inputMessage.trim() === '' || isTyping}
                  style={{
                    background: inputMessage.trim() === '' || isTyping ? 'rgba(255, 255, 255, 0.2)' : '#fff',
                    color: inputMessage.trim() === '' || isTyping ? 'rgba(255, 255, 255, 0.6)' : '#667eea',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: inputMessage.trim() === '' || isTyping ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: inputMessage.trim() !== '' && !isTyping ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (inputMessage.trim() !== '' && !isTyping) {
                      e.target.style.background = '#f8fafc';
                      e.target.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (inputMessage.trim() !== '' && !isTyping) {
                      e.target.style.background = '#fff';
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                  title="Send message"
                >
                  <i className="fas fa-arrow-up" style={{ fontSize: '0.9rem' }}></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistant; 